import React, { useEffect, useMemo, useState } from "react";
import { Form, InputNumber, Modal, Select, Space, Spin, Typography, notification } from "antd";
import { useTranslation } from "react-i18next";
import EndPoints from "../../../services/end-points";
import OrderProductService from "../../../services/order-product";
import { httpclient } from "../../../services/http-client";
import { IOrderProductBatchWithdrawal } from "../../../models/order-product/types";
import { IBatch } from "../../../models/wms/batch/response";
import getApiErrorMessage from "../../../utils/api/get-api-error-message";

interface Props {
    visible: boolean;
    mode: "add" | "update";
    productOrderId?: number;
    productId?: number;
    storeId?: number;
    withdrawals?: IOrderProductBatchWithdrawal[] | null;
    initialBatchId?: number;
    initialQuantity?: number;
    onCancel: () => void;
    onSuccess: () => void;
}

const BatchAllocationModal: React.FC<Props> = ({
    visible,
    mode,
    productOrderId,
    productId,
    storeId,
    withdrawals,
    initialBatchId,
    initialQuantity,
    onCancel,
    onSuccess,
}) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [loadingBatches, setLoadingBatches] = useState(false);
    const [batches, setBatches] = useState<IBatch[]>([]);
    const [selectedBatch, setSelectedBatch] = useState<IBatch | null>(null);
    const [orderProductService] = useState(() => new OrderProductService(httpclient));

    const linkedBatchIds = useMemo(
        () => new Set((withdrawals ?? []).map((w) => w.batchId)),
        [withdrawals]
    );

    const loadBatches = async () => {
        if (!productId || !storeId) return;
        setLoadingBatches(true);
        try {
            const { data } = await EndPoints.wmsBatch.getAllBatches({
                productId,
                storeId,
                hasStock: mode === "add" ? true : undefined,
                perPage: -1,
            });
            setBatches(data?.data ?? []);
        } catch (error: any) {
            notification.error({
                message: t("error"),
                description: getApiErrorMessage(error, t("failed_to_load_batches")),
            });
        } finally {
            setLoadingBatches(false);
        }
    };

    const loadSelectedBatch = async () => {
        if (!initialBatchId) return;
        setLoadingBatches(true);
        try {
            const { data } = await EndPoints.wmsBatch.getBatch(initialBatchId);
            setSelectedBatch(data ?? null);
        } catch (error: any) {
            notification.error({
                message: t("error"),
                description: getApiErrorMessage(error, t("failed_to_load_batches")),
            });
        } finally {
            setLoadingBatches(false);
        }
    };

    useEffect(() => {
        if (!visible) return;
        form.resetFields();
        setSelectedBatch(null);
        if (mode === "add") {
            loadBatches();
        } else {
            form.setFieldsValue({
                batchId: initialBatchId,
                quantity: initialQuantity,
            });
            loadSelectedBatch();
        }
    }, [visible, productId, storeId, mode, initialBatchId, initialQuantity]);

    const batchOptions = useMemo(() => {
        const eligibleBatches = mode === "update"
            ? batches.filter((b) => linkedBatchIds.has(b.id))
            : batches;

        return eligibleBatches.map((b) => ({
            value: b.id,
            label: b.expirationDate
                ? `${b.batchNumber ?? "#" + b.id} - ${b.expirationDate} (${t("available")}: ${b.availableQuantity})`
                : `${b.batchNumber ?? "#" + b.id} (${t("available")}: ${b.availableQuantity})`,
        }));
    }, [batches, mode, linkedBatchIds, t]);

    const handleSubmit = async () => {
        if (!productOrderId) return;
        try {
            const values = await form.validateFields();
            const quantity = Number(values.quantity);

            if (mode === "add" && quantity <= 0) {
                notification.error({
                    message: t("validation_error"),
                    description: t("batch_add_quantity_must_be_positive"),
                });
                return;
            }

            if (mode === "update" && quantity < 0) {
                notification.error({
                    message: t("validation_error"),
                    description: t("batch_update_quantity_must_be_non_negative"),
                });
                return;
            }

            const batchId = mode === "update" ? initialBatchId : values.batchId;

            if (!batchId) {
                notification.error({
                    message: t("validation_error"),
                    description: t("please_select_batch"),
                });
                return;
            }

            if (mode === "update" && !linkedBatchIds.has(batchId)) {
                notification.error({
                    message: t("validation_error"),
                    description: t("batch_not_linked_to_order_line"),
                });
                return;
            }

            setLoading(true);
            if (mode === "add") {
                await orderProductService.addBatchAllocation({
                    productOrderId,
                    batchId,
                    quantity,
                });
            } else {
                await orderProductService.updateBatchAllocation({
                    productOrderId,
                    batchId,
                    quantity,
                });
            }

            notification.success({
                message: t("success"),
                description: mode === "add"
                    ? t("batch_allocation_added_successfully")
                    : t("batch_allocation_updated_successfully"),
            });
            onSuccess();
        } catch (error: any) {
            if (error?.errorFields) return;

            notification.error({
                message: t("error"),
                description: getApiErrorMessage(error, t("failed_to_update_batch_allocation")),
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={visible}
            onCancel={onCancel}
            onOk={handleSubmit}
            width="min(760px, 92vw)"
            okText={mode === "add" ? t("add_from_batch") : t("adjust_batch_allocation")}
            cancelText={t("cancel")}
            confirmLoading={loading}
            okButtonProps={{ disabled: loadingBatches }}
            title={mode === "add" ? t("add_from_specific_batch") : t("adjust_existing_batch_allocation")}
            styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
        >
            <Spin spinning={loadingBatches}>
                <Space direction="vertical" style={{ width: "100%" }} size="middle">
                    <Typography.Text type="secondary">
                        {mode === "add"
                            ? t("batch_quantity_product_units_hint")
                            : t("batch_target_quantity_product_units_hint")}
                    </Typography.Text>
                    <Form form={form} layout="vertical">
                        {mode === "add" ? (
                            <Form.Item
                                label={t("batch")}
                                name="batchId"
                                rules={[{ required: true, message: t("please_select_batch") }]}
                            >
                                <Select
                                    showSearch
                                    loading={loadingBatches}
                                    options={batchOptions}
                                    placeholder={t("select_batch")}
                                    optionFilterProp="label"
                                />
                            </Form.Item>
                        ) : (
                            <>
                                <Form.Item name="batchId" hidden>
                                    <InputNumber />
                                </Form.Item>
                                <Form.Item label={t("batch")}>
                                    <Typography.Text>
                                        {selectedBatch?.batchNumber ?? (initialBatchId ? `#${initialBatchId}` : "-")}
                                        {selectedBatch?.expirationDate ? ` - ${selectedBatch.expirationDate}` : ""}
                                    </Typography.Text>
                                </Form.Item>
                                <Space size="large" wrap>
                                    <Typography.Text type="secondary">
                                        {t("entered_quantity")}: {selectedBatch?.enteredQuantityByUnit ?? "-"}
                                    </Typography.Text>
                                    <Typography.Text type="secondary">
                                        {t("withdrawn_quantity")}: {selectedBatch?.withdrawnQuantityByUnit ?? initialQuantity ?? "-"}
                                    </Typography.Text>
                                    <Typography.Text type="secondary">
                                        {t("available_quantity")}: {selectedBatch?.availableQuantityByUnit ?? selectedBatch?.availableQuantity ?? "-"}
                                    </Typography.Text>
                                </Space>
                            </>
                        )}
                        <Form.Item
                            label={t("quantity")}
                            name="quantity"
                            rules={[
                                { required: true, message: t("please_enter_quantity") },
                            ]}
                        >
                            <InputNumber
                                style={{ width: "100%" }}
                                min={mode === "add" ? 0.000001 : 0}
                                step={0.01}
                                stringMode
                                placeholder={t("enter_quantity_in_product_units")}
                            />
                        </Form.Item>
                    </Form>
                </Space>
            </Spin>
        </Modal>
    );
};

export default BatchAllocationModal;
